import { useCallback } from 'react';

import { buildCopilotPlacementTargets } from 'app/modules/wallforming/components/copilot/utils/buildCopilotPlacementTargets';
import {
    buildCopilotApplicationContext,
    getCopilotCapabilityCatalog,
    getCopilotRelevantCommandIds
} from 'app/modules/wallforming/components/copilot/utils/copilotCapabilities';
import { getConnectorRelatedPoiIndices } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { buildSolveCornerPlacementRequest } from 'app/modules/wallforming/components/setting-container/corner-placement/utils/buildSolveCornerPlacementRequest';
import { buildSolvePanelPlacementRequest } from 'app/modules/wallforming/components/setting-container/panel-placement/utils/buildSolvePanelPlacementRequest';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID } from 'shared/constants';

import type { CopilotChatRequest, CopilotMode } from 'app/modules/wallforming/components/copilot/types/copilotTypes';

const SYSTEM_ID = import.meta.env.VITE_DEFAULT_SYSTEM_ID;

interface CopilotRequestBuilderProps {
    conversationHistory: CopilotChatRequest['history'];
    mode: CopilotMode;
    sessionId: string;
}

export const useCopilotRequestBuilder = ({ conversationHistory, mode, sessionId }: CopilotRequestBuilderProps) => {
    return useCallback(
        (userPrompt: string): CopilotChatRequest | null => {
            const state = usePlacementStore.getState();
            const selectedPoiIndex = state.poiSelection.current.index;
            const poiIndex =
                selectedPoiIndex !== undefined && state.pois[selectedPoiIndex]?.poi.poiGuid !== EMPTY_GUID
                    ? selectedPoiIndex
                    : state.pois.findIndex(({ poi }) => poi.poiGuid !== EMPTY_GUID);
            const poi = poiIndex < 0 ? undefined : state.pois[poiIndex];
            if (!SYSTEM_ID) {
                return null;
            }

            const capabilities = mode === 'agent' ? getCopilotCapabilityCatalog(userPrompt) : [];
            const placementTargets = buildCopilotPlacementTargets(state, sessionId, SYSTEM_ID, mode === 'agent');
            const applicationContextPrompt = [
                ...conversationHistory
                    .filter((message) => message.role === 'user')
                    .slice(-2)
                    .map((message) => message.content),
                userPrompt
            ].join('\n');

            const relevantCommandIds = getCopilotRelevantCommandIds(applicationContextPrompt);
            const useCompactApplicationContext =
                mode === 'agent' &&
                capabilities.length === 0 &&
                placementTargets.length > 0 &&
                relevantCommandIds?.length === 0;

            return {
                prompt: userPrompt,
                mode,
                history: conversationHistory,
                context: {
                    workspaceRevision: state.workspaceRevision,
                    application: buildCopilotApplicationContext(
                        state,
                        poiIndex >= 0 ? poiIndex : undefined,
                        !useCompactApplicationContext,
                        relevantCommandIds,
                        applicationContextPrompt
                    ),
                    capabilities,
                    sessionId,
                    systemId: SYSTEM_ID,
                    poiId: poi?.poi.poiGuid ?? EMPTY_GUID,
                    relatedPoiIds:
                        mode === 'agent' && poi
                            ? getConnectorRelatedPoiIndices(state.pois, poiIndex)
                                  .map((index) => state.pois[index]?.poi.poiGuid)
                                  .filter((id): id is string => id !== undefined && id !== EMPTY_GUID)
                            : [],
                    placementTargets,
                    ...(mode === 'agent' && poi
                        ? {
                              panelPlacement: buildSolvePanelPlacementRequest({
                                  state,
                                  sessionId,
                                  systemId: SYSTEM_ID,
                                  poiIndex
                              }),
                              cornerPlacement: buildSolveCornerPlacementRequest({
                                  state,
                                  sessionId,
                                  systemId: SYSTEM_ID,
                                  poiIndex
                              })
                          }
                        : {})
                }
            };
        },
        [conversationHistory, mode, sessionId]
    );
};
